import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BASE_PATH } from './variables';

export type FlashSaleScopeType = 'ALL_STORES_AND_WEB' | 'SELECTED_STORES';

export interface FlashSaleStoreRef {
  physicalShopId: number;
  physicalShopName?: string;
  warehouseId?: number | null;
}

export interface FlashSaleLine {
  priceItemId?: number;
  skuCode: string;
  skuName?: string | null;
  amount: number;
  pendingAmount?: number | null;
  pendingAmountStatus?: string | null;
  quotaTotal?: number;
  quotaRemaining?: number;
  quota?: number;
  currentStock?: number | null;
  maxAllowedQuota?: number | null;
}

export interface FlashSaleCampaign {
  id: number;
  campaignName: string;
  scopeType: FlashSaleScopeType;
  scopeLabel?: string;
  stores?: FlashSaleStoreRef[];
  startAt: string;
  endAt: string;
  scheduleStatus: string;
  countdownVisible: boolean;
  countdownSeconds?: number | null;
  t2hAlertSentAt?: string | null;
  lines?: FlashSaleLine[];
  skuCodes?: string[];
}

export interface FlashSaleCampaignCreate {
  campaignName: string;
  scopeType: FlashSaleScopeType;
  physicalShopIds?: number[];
  startAt: string;
  endAt: string;
  lines: { skuCode: string; skuName?: string; amount: number; quota: number }[];
}

export interface FlashSaleCampaignUpdate {
  campaignName?: string;
  scopeType?: FlashSaleScopeType;
  physicalShopIds?: number[];
  startAt?: string;
  endAt?: string;
  lines?: { skuCode: string; skuName?: string; amount: number; quota: number }[];
}

export interface FlashSaleAlert {
  id: number;
  priceItemId?: number | null;
  skuCode: string;
  campaignName?: string | null;
  amount: number;
  startsAt: string;
  message: string;
  read: boolean;
  createdAt: string;
}

export interface FlashSaleStockPreview {
  maxStockPercent: number;
  lines: { skuCode: string; currentStock: number; maxAllowedQuota: number }[];
}

export interface FlashSaleQuotaPolicy {
  id: number;
  name: string;
  maxStockPercent: number;
  active: boolean;
}

export interface FlashSaleItem {
  id: number;
  campaignId?: number | null;
  skuCode: string;
  campaignName?: string | null;
  amount: number;
  pendingAmount?: number | null;
  pendingAmountStatus?: string | null;
  currencyCode?: string;
  startAt: string;
  endAt: string;
  quotaTotal: number;
  quotaRemaining: number;
  scheduleStatus: string;
}

@Injectable({ providedIn: 'root' })
export class FlashSaleApiService {
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);

  listCampaigns(): Observable<FlashSaleCampaign[]> {
    return this.http.get<FlashSaleCampaign[]>(`${this.basePath}/v1/flash-sales`);
  }

  getCampaign(id: number): Observable<FlashSaleCampaign> {
    return this.http.get<FlashSaleCampaign>(`${this.basePath}/v1/flash-sales/${id}`);
  }

  createCampaign(body: FlashSaleCampaignCreate): Observable<FlashSaleCampaign> {
    return this.http.post<FlashSaleCampaign>(`${this.basePath}/v1/flash-sales`, body);
  }

  updateCampaign(id: number, body: FlashSaleCampaignUpdate): Observable<FlashSaleCampaign> {
    return this.http.put<FlashSaleCampaign>(`${this.basePath}/v1/flash-sales/${id}`, body);
  }

  deleteCampaign(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/v1/flash-sales/${id}`);
  }

  previewStock(body: {
    scopeType: FlashSaleScopeType;
    physicalShopIds?: number[];
    skuCodes: string[];
  }): Observable<FlashSaleStockPreview> {
    return this.http.post<FlashSaleStockPreview>(
      `${this.basePath}/v1/flash-sales/stock-preview`,
      body,
    );
  }

  listAlerts(unreadOnly = false): Observable<FlashSaleAlert[]> {
    const params = new HttpParams().set('unreadOnly', String(unreadOnly));
    return this.http.get<FlashSaleAlert[]>(`${this.basePath}/v1/flash-sales/alerts`, {
      params,
    });
  }

  markAlertRead(alertId: number): Observable<void> {
    return this.http.post<void>(
      `${this.basePath}/v1/flash-sales/alerts/${alertId}/read`,
      {},
    );
  }

  listItems(): Observable<FlashSaleItem[]> {
    return this.http.get<FlashSaleItem[]>(`${this.basePath}/v1/flash-sales/items`);
  }

  requestAmount(priceItemId: number, amount: number): Observable<FlashSaleItem> {
    return this.http.put<FlashSaleItem>(
      `${this.basePath}/v1/flash-sales/items/${priceItemId}/amount`,
      { amount },
    );
  }

  approveAmount(priceItemId: number): Observable<FlashSaleItem> {
    return this.http.post<FlashSaleItem>(
      `${this.basePath}/v1/flash-sales/items/${priceItemId}/amount/approve`,
      {},
    );
  }

  listQuotaPolicies(): Observable<FlashSaleQuotaPolicy[]> {
    return this.http.get<FlashSaleQuotaPolicy[]>(
      `${this.basePath}/v1/flash-sale-quota-policies`,
    );
  }

  createQuotaPolicy(body: {
    name: string;
    maxStockPercent: number;
    activeFlag?: boolean;
  }): Observable<FlashSaleQuotaPolicy> {
    return this.http.post<FlashSaleQuotaPolicy>(
      `${this.basePath}/v1/flash-sale-quota-policies`,
      body,
    );
  }

  updateQuotaPolicy(
    id: number,
    body: { name: string; maxStockPercent: number; activeFlag?: boolean },
  ): Observable<FlashSaleQuotaPolicy> {
    return this.http.put<FlashSaleQuotaPolicy>(
      `${this.basePath}/v1/flash-sale-quota-policies/${id}`,
      body,
    );
  }

  deleteQuotaPolicy(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/v1/flash-sale-quota-policies/${id}`);
  }
}
